import { toast } from '@heroui/react'
import { v4 as uuid } from 'uuid'
import { create } from 'zustand'

import { reportsApi } from '@/api/reports.api'
import i18n from '@/i18n'
import type { ModifyReportRequest, Report as ApiReport } from '@/types/report'
import type { Report as UiReport, ReportStatus } from '@/types/report.types'
import { getAppSettings, updateAppSettings } from '@/utils/settings'
import { SortDirection, type TableSort } from '@/utils/sorting'

interface ReportsStore {
  reports: UiReport[];
  totalGross: number;
  isLoading: boolean;
  error: string | null;
  filters: {
    driverID: string;
    vehicleID: string;
    from: string;
    to: string;
    document: string;
  };
  pagination: {
    sort: TableSort | null;
    page: number;
    limit: number;
    totalPages: number;
    total: number;
  };

  fetchReports: () => Promise<void>;
  addReport: (data: ModifyReportRequest) => Promise<UiReport>;
  updateReport: (id: string, data: ModifyReportRequest) => Promise<void>;
  deleteReport: (id: string) => Promise<void>;
  duplicateReport: (id: string) => Promise<string>;

  setFilters: (filters: Partial<ReportsStore['filters']>) => void;
  setPagination: (pagination: Partial<ReportsStore['pagination']>) => void;
  resetFilters: () => void;
  clearError: () => void;
}

export const mapApiToUi = (r: ApiReport): UiReport => ({
  id: r.ID,
  identifier: r.Identifier ?? '',
  companyName: (r as any).CompanyName ?? 'WEST CONTINENT Sp.z.o.o.',
  reportNumber: (r as any).ReportNumber ?? '',
  driverId: r.Driver?.ID ?? '',
  vehicleId: r.Vehicle?.ID ?? '',
  driverName: r.Driver?.Name ?? '',
  driverPhone: r.Driver?.Phone ?? '',
  vehiclePlate: r.Vehicle?.Number ?? '',
  vehicleBrand: r.Vehicle?.Model ?? '',
  dateFrom: r.StartDate,
  dateTo: r.EndDate,
  settlementDate: (r as any).SettlementDate,
  status: ((r as any).Status ?? 'pending') as ReportStatus,
  rows: (r.Items ?? []).map(row => ({
    id: uuid(),
    date: row.Date,
    type: row.Expense,
    documentNumber: row.Document,
    country: (row.Country ?? '').toUpperCase(),
    amount: row.Cost,
    currency: (row.Currency ?? '').toUpperCase(),
    exchangeRate: row.Rate,
    nbpTable: row.RateDate ?? '',
    amountPLN: row.Total,
    isIncluded: row.Exclude !== true,
  })),
  totalGrossPLN: r.TotalGross ?? (r as any).Total ?? 0,
  totalNetPLN: r.TotalNet ?? 0,
  totalGrossEUR: r.TotalGrossEUR ?? 0,
  totalNetEUR: r.TotalNetEUR ?? 0,
  paidRoads: (r.PaidRoads ?? []).map(road => ({
    id: road.ID,
    date: road.Date,
    cost: road.Cost,
    country: road.Country,
  })),
  paidRoadsTotal: r.PaidRoadsTotal ?? 0,
  fuel: (r.Fuel ?? []).map(fuel => ({
    id: fuel.ID,
    date: fuel.Date,
    liters: fuel.Liters,
    cost: fuel.Cost,
    country: fuel.Country,
    type: fuel.Type,
  })),
  fuelTotal: r.FuelTotal ?? 0,
  fuelLitersTotal: r.FuelLitersTotal ?? 0,
  createdAt: (r as any).CreatedAt ?? '',
  updatedAt: (r as any).UpdatedAt ?? '',
})


export const useReportsStore = create<ReportsStore>((set, get) => ({
  reports: [],
  totalGross: 0,
  isLoading: false,
  error: null,
  filters: {
    driverID: '',
    vehicleID: '',
    from: '',
    to: '',
    document: '',
  },
  pagination: {
    sort: { key: 'createdAt', direction: SortDirection.DESC },
    page: 0,
    limit: getAppSettings().reportsLimit ?? 10,
    totalPages: 0,
    total: 0,
  },

  fetchReports: async () => {
    const { filters, pagination } = get()
    set({ isLoading: true, error: null })

    const params = {
      driverID: filters.driverID || undefined,
      vehicleID: filters.vehicleID || undefined,
      from: filters.from || undefined,
      to: filters.to || undefined,
      document: filters.document || undefined,
      page: pagination.page,
      limit: pagination.limit,
      // sort: pagination.sort ? [sorting(pagination.sort)] : undefined,
    }

    try {
      const response = await reportsApi.getReports(params)
      set({
        reports: (response.Reports ?? []).map(mapApiToUi),
        totalGross: response.TotalGross ?? 0,
        pagination: {
          ...pagination,
          totalPages: Math.ceil(response.Pagination.Total / pagination.limit),
          total: response.Pagination.Total,
        },
        isLoading: false,
      })
    } catch (err) {
      const msg = (err as Error).message
      set({ error: msg, isLoading: false })
      toast.danger(i18n.t('reports.toast.fetch_error'), { description: msg })
    }
  },

  addReport: async data => {
    set({ isLoading: true, error: null })
    try {
      const report = await reportsApi.createReport(data)
      const uiReport = mapApiToUi(report)
      toast.success(i18n.t('reports.toast.create_success'))

      await get().fetchReports()
      return uiReport
    } catch (err) {
      toast.danger(i18n.t('reports.toast.create_error'), { description: (err as Error).message })
      set({ error: (err as Error).message, isLoading: false })
      throw err
    }
  },

  updateReport: async (id, data) => {
    set({ isLoading: true, error: null })
    try {
      await reportsApi.updateReport(id, data)
      toast.success(i18n.t('reports.toast.update_success'))

      await get().fetchReports()
    } catch (err) {
      toast.danger(i18n.t('reports.toast.update_error'), { description: (err as Error).message })
      set({ error: (err as Error).message, isLoading: false })
      throw err
    }
  },

  deleteReport: async id => {
    set({ isLoading: true, error: null })
    try {
      await reportsApi.deleteReport(id)
      toast.success(i18n.t('reports.toast.delete_success'))

      await get().fetchReports()
    } catch (err) {
      toast.danger(i18n.t('reports.toast.delete_error'), { description: (err as Error).message })
      set({ error: (err as Error).message, isLoading: false })
      throw err
    }
  },

  duplicateReport: async id => {
    set({ isLoading: true, error: null })
    try {
      const response = await reportsApi.duplicateReport(id)
      const newId = response.id
      toast.success(i18n.t('reports.toast.create_success'))

      await get().fetchReports()
      return newId
    } catch (err) {
      toast.danger(i18n.t('reports.toast.create_error'), { description: (err as Error).message })
      set({ error: (err as Error).message, isLoading: false })
      throw err
    }
  },

  setFilters: newFilters => {
    set(state => ({
      filters: { ...state.filters, ...newFilters },
      pagination: { ...state.pagination, page: 0 },
    }))
    get().fetchReports()
  },

  setPagination: newPagination => {
    set(state => {
      const updatedPagination = { ...state.pagination, ...newPagination }
      if (newPagination.limit !== undefined) {
        updateAppSettings({ reportsLimit: newPagination.limit })
      }
      return { pagination: updatedPagination }
    })
    get().fetchReports()
  },

  resetFilters: () => {
    const initialLimit = getAppSettings().reportsLimit ?? 10
    set({
      filters: {
        driverID: '',
        vehicleID: '',
        from: '',
        to: '',
        document: '',
      },
      pagination: {
        sort: { key: 'createdAt', direction: SortDirection.DESC },
        page: 0,
        limit: initialLimit,
        totalPages: 1,
        total: 0,
      },
    })
    get().fetchReports()
  },

  clearError: () => set({ error: null }),
}))
